import React, { useEffect } from "react";
import { toast } from "sonner";
import cn from "classnames";

// component
import { Icon } from "@trussworks/react-uswds";
import {
  Dialog,
  DialogContent,
  DialogHeading,
  DialogClose,
  IconButton,
  Button,
  Checkbox,
} from "components/LibraryComponents/Components";
import Accordion from "./Accordion";

// constants
import { Permissions } from "constants/permissions";

// utils
import { DefaultExportColumns, ExportColumns, exportData } from "utils/export";

// store
import useExportColumnStore from "store/useExportColumnStore";
import useAuthStore from "store/useAuthStore";

// styles
import styles from "./ExportResultsDialog.module.scss";

// types
import { BusinessItem } from "types/results-panel.types";

/**
 * This function checks if the column is not a default column
 * @param id The id of the column object
 * @returns true if the id is not represented in the exportModelCheckBoxData array
 */
const isNonDefaultColumn = (id: string): boolean => {
  return !DefaultExportColumns.some((item) => item.id === id);
};

type DialogProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: BusinessItem[];
};

// `ExportResultsDialog` component is an updated version of the `ExportModal` component
export default function ExportResultsDialog(props: DialogProps) {
  const { exportColumns, setExportColumns } = useExportColumnStore();
  const { user } = useAuthStore();

  const role = user?.role ?? "guest"
  const privileged = Permissions[role]?.privilegedSearch ?? false;

  const protectedExportColumns = ExportColumns.filter(
    (item) => privileged || item.visible
  );

  const isCheckboxDisabled = exportColumns.length === 20;

  const nonDefaultSelectedColumns = exportColumns.filter((item) =>
    isNonDefaultColumn(item.value)
  );

  // on mount
  useEffect(() => {
    if (exportColumns.length === 0) {
      setExportColumns(
        DefaultExportColumns.map((item) => ({
          label: item.label,
          value: item.name,
        }))
      );
    }
  }, []);

  const handleChange = (name: string, label: string, checked: boolean) => {
    if (checked) {
      const newList = [...exportColumns, { label, value: name }];

      setExportColumns(newList);
    } else {
      const filterList = exportColumns.filter((item) => item.value !== name);

      setExportColumns(filterList);
    }
  };

  const handleExport = () => {
    if (exportColumns.length === 0) {
      toast.error("Please select at least one column");
    } else {
      exportData(props.data, exportColumns);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.setOpen}>
      <DialogContent
        className={styles["export-dialog-root"]}
        hasBackdrop
        lockScroll
      >
        <div className={styles["export-dialog-body"]}>
          <div className={styles["header"]}>
            <DialogHeading className={styles["heading"]}>
              Edit export columns
            </DialogHeading>
            <IconButton
              type="button"
              Icon={<Icon.Close aria-hidden="true" />}
              size="sm"
              onClick={() => props.setOpen(false)}
              tabIndex={-1}
              aria-label="Close dialog"
            />
          </div>

          <div className={styles["export-content"]}>
            <p className={styles["desc"]}>
              Choose up to 20 table columns that will appear in your spreadsheet
              export.
            </p>

            <h3 className={styles["sub-heading"]}>Default columns</h3>

            <ul className={styles["default-list"]}>
              {DefaultExportColumns.map((item) => {
                const isChecked = exportColumns
                  .map((item) => item.value)
                  .includes(item.name);

                return (
                  <li key={item.name} className={styles["default-item"]}>
                    <Checkbox
                      id={`export-${item.name}`}
                      name={item.name}
                      label={item.label}
                      labelDescription={item.labelDescription}
                      onChange={(e) => {
                        handleChange(item.name, item.label, e.target.checked);
                      }}
                      checked={isChecked}
                      disabled={
                        item.name === "legal_business_name" ||
                        (isCheckboxDisabled && !isChecked)
                      }
                    />
                  </li>
                );
              })}
            </ul>

            {/* Selected columns */}
            {nonDefaultSelectedColumns.length > 0 && (
              <div className={styles["selected-columns"]}>
                <p className={styles["sub-heading"]}>Selected columns</p>
                <ul className={styles["selected-list"]}>
                  {nonDefaultSelectedColumns.map((item) => {
                    const isChecked = exportColumns
                      .map((item) => item.value)
                      .includes(item.value);

                    return (
                      <li key={item.value} className={styles["selected-item"]}>
                        <Checkbox
                          id={`export-${item.value}`}
                          name={item.value}
                          label={item.label}
                          onChange={(e) =>
                            handleChange(
                              item.value,
                              item.label,
                              e.target.checked
                            )
                          }
                          checked={isChecked}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Export columns */}
            <div className={styles["accordion-list"]}>
              {protectedExportColumns.map((item) => (
                <Accordion key={item.name} name={item.name}>
                  {item.columns && (
                    <ul className={styles["list"]}>
                      {item.columns.map((column) => {
                        const isChecked = exportColumns
                          .map((item) => item.value)
                          .includes(column.name);

                        return (
                          <li key={column.name} className={styles["item"]}>
                            <Checkbox
                              id={`export-${column.name}`}
                              name={column.name}
                              label={column.label}
                              labelDescription={column.labelDescription}
                              onChange={(e) => {
                                handleChange(
                                  column.name,
                                  column.label,
                                  e.target.checked
                                );
                              }}
                              checked={isChecked}
                              disabled={isCheckboxDisabled && !isChecked}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {item.subColumns && (
                    <div>
                      <p className={styles["sub-title"]}>SBA Certifications</p>

                      {item.subColumns.map((subItem, idx) => (
                        <div key={idx} className={styles["sub-columns"]}>
                          <p className={styles["sub-name"]}>{subItem.name}</p>
                          <ul className={styles["list"]}>
                            {subItem.columns.map((column) => {
                              const isChecked = exportColumns
                                .map((item) => item.value)
                                .includes(column.name);

                              return (
                                <li
                                  key={column.name}
                                  className={cn(styles["item"], {
                                    // Add divider class to the NFG
                                    [styles["divider"]]:
                                      column.name ===
                                      "non_fed_govt_certifications",
                                  })}
                                >
                                  <Checkbox
                                    id={`export-${column.name}`}
                                    name={column.name}
                                    label={column.label}
                                    labelDescription={column.labelDescription}
                                    onChange={(e) =>
                                      handleChange(
                                        column.name,
                                        column.label,
                                        e.target.checked
                                      )
                                    }
                                    checked={isChecked}
                                    disabled={isCheckboxDisabled && !isChecked}
                                  />
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </Accordion>
              ))}
            </div>
          </div>
        </div>

        <div className={styles["export-dialog-footer"]}>
          <DialogClose
            className={styles["btn-close"]}
            aria-label="Close dialog"
          >
            Cancel and close
          </DialogClose>

          <Button
            type="submit"
            className={styles["btn-submit"]}
            onClick={handleExport}
            aria-label="Export results to CSV"
          >
            Export results to .csv
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
